import { useCallback, useEffect, useRef } from "react";
import type { Ad, TrackEventsRequest } from "@b2bportal/media-ads-api";
import { EventType } from "@b2bportal/media-ads-api";
import { trackAdEvents } from "../api/mediaAds";

/** Default IntersectionObserver threshold for impression tracking (50%). */
const DEFAULT_IMPRESSION_THRESHOLD = 0.5;

/**
 * Minimum continuous dwell time (ms) the ad must remain visible before an
 * impression is recorded. Per MRC/IAB display viewability guidelines, a
 * display ad must be at least 50% visible for at least 1 continuous second.
 */
const DEFAULT_IMPRESSION_DWELL_MS = 1000;

/**
 * Reports events without ever letting a tracking problem reach the render tree.
 *
 * `trackAdEvents` can throw synchronously — `getAxiosInstance` does exactly that
 * when `setMediaAxiosInstance` has not been called, e.g. in Storybook or a unit
 * test — so a bare `trackAdEvents(...).catch(...)` would escape the effect and
 * take the component down with it. Ad tracking is never worth a broken page.
 */
const reportEvents = (
  events: TrackEventsRequest["events"],
  description: string
): Promise<void> =>
  Promise.resolve()
    .then(() => trackAdEvents({ events }))
    .then(() => undefined)
    .catch((e) => console.error(`Failed to track ${description}`, e));

export interface UseAdTrackingOptions {
  /**
   * IntersectionObserver threshold (0–1) at which visibility begins.
   * Defaults to 0.5 (50% of the ad element must be in the viewport).
   */
  impressionThreshold?: number;
  /**
   * Time in milliseconds the ad must remain continuously visible before
   * an impression event fires. Defaults to 1000 (1 second) per IAB/MRC
   * viewability standards.
   */
  impressionDwellMs?: number;
}

/**
 * Handles insertion, impression, and click tracking for HTS Media ads.
 *
 * - **Insertion** — fires automatically when the ad mounts (enters the DOM).
 *   Maps to ads-orchestrator's `TRACKING_EVENT_TYPE_RENDERED`.
 * - **Impression** — fires when the ad is at least 50% visible in the
 *   viewport for 1 continuous second (MRC/IAB display viewability standard).
 *   Uses IntersectionObserver + a dwell timer. Requires the returned `adRef`
 *   to be attached to the ad's container element. Both the threshold and
 *   dwell time are configurable via options.
 * - **Click** — fires on demand via the returned `trackClick` function, which
 *   resolves once the event has been sent so callers can await it before
 *   navigating away.
 *
 * Each event type fires at most once per unique `ad.trackingData` value.
 *
 * @example
 * ```tsx
 * const { adRef, trackClick } = useAdTracking(ad);
 * return (
 *   <div ref={adRef} onClick={() => trackClick()}>
 *     <img src={ad.creative.imageUrl} />
 *   </div>
 * );
 * ```
 */
export const useAdTracking = <TElement extends HTMLElement = HTMLElement>(
  ad: Ad | undefined,
  options?: UseAdTrackingOptions
) => {
  const threshold =
    options?.impressionThreshold ?? DEFAULT_IMPRESSION_THRESHOLD;
  const dwellMs = options?.impressionDwellMs ?? DEFAULT_IMPRESSION_DWELL_MS;
  const adRef = useRef<TElement | null>(null);
  const trackedInsertionRef = useRef<string | undefined>();
  const trackedImpressionRef = useRef<string | undefined>();

  // Track insertion when ad renders in the DOM
  useEffect(() => {
    if (ad == null) return;
    if (trackedInsertionRef.current === ad.trackingData) return;

    trackedInsertionRef.current = ad.trackingData;

    void reportEvents(
      [
        {
          eventType: EventType.Insertion,
          trackingData: { token: ad.trackingData },
          occurredAt: new Date().toISOString(),
        },
      ],
      "insertion event"
    );
  }, [ad?.trackingData]);

  // Track impression when ad is continuously visible for the dwell period.
  // MRC/IAB standard: 50 % of pixels in view for ≥ 1 continuous second.
  useEffect(() => {
    if (ad == null) return;
    if (trackedImpressionRef.current === ad.trackingData) return;

    const element = adRef.current;
    if (element == null) return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (trackedImpressionRef.current === ad.trackingData) return;

        if (entry.isIntersecting) {
          // Start the dwell timer when the ad becomes visible
          dwellTimer = setTimeout(() => {
            trackedImpressionRef.current = ad.trackingData;
            observer.disconnect();

            void reportEvents(
              [
                {
                  eventType: EventType.Impression,
                  trackingData: { token: ad.trackingData },
                  occurredAt: new Date().toISOString(),
                },
              ],
              "impression event"
            );
          }, dwellMs);
        } else if (dwellTimer != null) {
          // Ad scrolled out of view before dwell period — reset
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => {
      if (dwellTimer != null) clearTimeout(dwellTimer);
      observer.disconnect();
    };
  }, [ad?.trackingData, threshold, dwellMs]);

  /**
   * Tracks a click. Resolves once the event has been sent, or immediately if
   * there is no ad to track. Never rejects, so callers can safely await this
   * before navigating away without a failed request blocking navigation.
   */
  const trackClick = useCallback(
    (targetAd?: Ad): Promise<void> => {
      const adToTrack = targetAd ?? ad;
      if (adToTrack == null) return Promise.resolve();

      return reportEvents(
        [
          {
            eventType: EventType.Click,
            trackingData: { token: adToTrack.trackingData },
            occurredAt: new Date().toISOString(),
          },
        ],
        "click event"
      );
    },
    [ad?.trackingData]
  );

  return { adRef, trackClick };
};

/**
 * Tracks insertion events for a batch of ads at once.
 * Useful for grid layouts where multiple ads are rendered simultaneously.
 */
export const trackBatchInsertions = (ads: Ad[]) => {
  if (ads.length === 0) return;

  void reportEvents(
    ads.map((ad) => ({
      eventType: EventType.Insertion,
      trackingData: { token: ad.trackingData },
      occurredAt: new Date().toISOString(),
    })),
    "insertion events"
  );
};

/**
 * Tracks impression events for a batch of ads at once.
 * Use when all ads are known to be visible (e.g., above the fold).
 * For viewport-based impression tracking, use `useAdTracking` with `adRef` instead.
 */
export const trackBatchImpressions = (ads: Ad[]) => {
  if (ads.length === 0) return;

  void reportEvents(
    ads.map((ad) => ({
      eventType: EventType.Impression,
      trackingData: { token: ad.trackingData },
      occurredAt: new Date().toISOString(),
    })),
    "impression events"
  );
};
